const refuelingReport = require('./refueling-report')
const traccarHelper = require('./util/traccar')
const odoo = require('./util/odoo')
const { devicesToProcess } = require('./util/device')
const { getTranslations, convertToLocaleString, getLanguage, convertMS, formatNumber, getDates } = require('./util/utils')
const { getStyle } = require('./reportStyle')
const { getUserPartner } = require('fleetmap-partners')
const { addTable, headerFromUser } = require('./util/pdfDocument')
const jsPDF = require('jspdf')
const { calculateConsumption } = require('./util/fuel')
const { getSummaryByDay } = require('./util/traccar')
const { getDataByDay } = require('./util/trips')

async function createFuelConsumptionReport (from, to, userData, traccar) {
  console.log('Create FuelConsumption Report')

  const reportData = []

  const allData = {
    devices: [],
    from,
    to
  }

  const devices = devicesToProcess(userData)

  const allInOne = await traccarHelper.getAllInOne(traccar, from, to, devices, true, true, true, false)
  const dates = getDates(from, to, userData.user.attributes.timezone || getUserPartner(userData.user).timezone)
  const summary = await getSummaryByDay(dates, userData, traccar, devices, true, from, to)

  const fuelServicesData = []
  if (devices.some(d => d.attributes.odooId)) {
    fuelServicesData.push(...(await odoo.getOdooFuelServices(traccar, from, to)))
  }

  if (allInOne.trips.length === 0) {
    return reportData
  }

  allData.totalDevices = 0

  for (const d of devices) {
    const stops = allInOne.stops.filter(t => t.deviceId === d.id)
    const trips = allInOne.trips.filter(t => t.deviceId === d.id)
    const route = allInOne.route.filter(r => r.deviceId === d.id)
    const deviceFuelServices = fuelServicesData.filter(f => Number.parseInt(d.attributes.odooId) === f.vehicle_id[0])

    if (trips.length > 0) {
      const refuelingPositions = await refuelingReport.calculateRefuelingPositions(userData, d, { route, trips, fuelServices: deviceFuelServices })
      const days = []
      for (const date of dates) {
        const { tripsByDay, stopsByDay, routeByDay, refuelingPositionsByDay } = await getDataByDay(d, date, { trips, stops, route, refuelingPositions }, userData, traccar)
        const summaryByDay = summary.find(a => a.date === date).summary
        const summaryByDevice = summaryByDay.find(a => a.deviceId === d.id)

        const distance = summaryByDevice.distance
        const spentFuel = calculateConsumption(d, { trips: tripsByDay, stops: stopsByDay, route: routeByDay, summary: summaryByDevice })

        const dataRow = {
          date,
          distance,
          spentFuel,
          avgConsumption: getCanAvgConsumption(distance, spentFuel),
          startOdometer: summaryByDevice.startOdometer,
          endOdometer: summaryByDevice.endOdometer,
          duration: tripsByDay.reduce((a, b) => a + b.duration, 0),
          refueling: (refuelingPositionsByDay && refuelingPositionsByDay.length > 0) ? refuelingPositionsByDay.reduce((a, b) => a + b.diff, 0) : 0
        }

        days.push(dataRow)
      }

      if (days.length > 0) {
        allData.devices.push({
          device: d,
          days
        })
        allData.totalDevices = allData.totalDevices + 1
      }
    }
  }

  reportData.push(allData)

  return reportData
}

function getOdooAvgConsumption (day, allRefuelingPositions, trips) {
  const refuelingData = allRefuelingPositions.filter(r => new Date(r.date).getTime() > new Date(day).getTime())
  let odooFuel
  let kms
  if (!refuelingData.length) {
    odooFuel = allRefuelingPositions.reduce((a, b) => a + b.diff, 0)
    kms = trips.reduce((a, b) => a + b.distance, 0)
  } else {
    odooFuel = allRefuelingPositions.filter(r => new Date(r.date).getTime() <= new Date(refuelingData[0].date).getTime()).reduce((a, b) => a + b.diff, 0)
    kms = trips.filter(t => new Date(t.startDate).getTime() < new Date(refuelingData[0].date).getTime()).reduce((a, b) => a + b.distance, 0)
  }

  if (kms === 0 || odooFuel === 0) { return { byKms: 0, byLiters: 0 } }

  return calculateAvgConsumption(odooFuel, kms)
}

function getCanAvgConsumption (distance, spentFuel) {
  if (distance === 0 || spentFuel === 0) { return { byKms: 0, byLiters: 0 } }

  return calculateAvgConsumption(spentFuel, distance)
}

function calculateAvgConsumption (fuel, distance) {
  return { byKms: fuel * 100 / (distance / 1000), byLiters: (distance / 1000) / fuel }
}

function exportFuelConsumptionReportToExcel (userData, reportData) {
  const translations = getTranslations(userData)
  const lang = getLanguage(userData)

  const settings = {
    sheetName: 'FuelConsumptionReport', // The name of the sheet
    fileName: 'FuelConsumptionReport' // The name of the spreadsheet
  }
  const headers = [
    { label: translations.report.vehicle, value: 'name' },
    { label: translations.report.date, value: 'date' },
    { label: translations.report.distance, value: 'kms' },
    { label: translations.report.consumption, value: 'consumption' },
    { label: (lang === 'pt-BR' ? translations.report.avg_consumption_by_liters : translations.report.avg_consumption), value: 'avg_consumption' },
    { label: translations.report.duration, value: 'duration' },
    { label: translations.report.odometer, value: 'accumulated_kms' },
    { label: translations.report.refueling, value: 'refueling' }
  ]
  let data = []
  if (reportData.devices) {
    reportData.devices.forEach((d) => {
      data = data.concat(d.days.map((r) => {
        return {
          name: d.device.name,
          date: r.date.toLocaleDateString(),
          kms: formatNumber(r.distance / 1000),
          consumption: r.spentFuel > 0 ? formatNumber(r.spentFuel) : 0,
          avg_consumption: formatNumber(lang === 'pt-BR' ? r.avgConsumption.byLiters : r.avgConsumption.byKms),
          duration: convertMS(r.duration),
          accumulated_kms: formatNumber((r.endOdometer / 1000)),
          refueling: formatNumber(r.refueling)
        }
      }))
      // Totals
      data = data.concat([{
        date: 'Total:' + d.days.length,
        kms: getTotalKms(userData.user, d.days),
        consumption: getTotalConsumption(userData.user, d.days),
        avg_consumption: getTotalAvgConsumption(userData.user, d.days),
        duration: getTotalDuration(userData.user, d.days),
        refueling: getTotalRefueling(userData.user, d.days)
      }])
      data = data.concat([{}])
    })
    return {
      headers,
      data,
      settings
    }
  }
}

async function exportFuelConsumptionReportToPDF (userData, reportData) {
  console.log('Export to PDF')
  const timezone = userData.user.attributes.timezone
  const translations = getTranslations(userData)
  const lang = getLanguage(userData)

  // eslint-disable-next-line new-cap
  const doc = new jsPDF.jsPDF('l')
  await headerFromUser(doc, translations.report.titleFuelConsumptionReport, userData.user)

  const headers = [
    translations.report.date,
    translations.report.distance,
    translations.report.consumption,
    (lang === 'pt-BR' ? translations.report.avg_consumption_by_liters : translations.report.avg_consumption),
    translations.report.duration,
    translations.report.odometer,
    translations.report.refueling
  ]

  if (reportData.devices) {
    reportData.devices.forEach((d, index) => {
      const data = []
      const name = deviceName(d.device)
      const group = userData.groups.find(g => d.device.groupId === g.id)

      const space = index === 0 ? 8 : 0
      if (index) {
        doc.addPage()
      }

      doc.setFontSize(13)
      doc.text(name, 20, space + 20)
      doc.setFontSize(11)
      doc.text(group ? translations.report.group + ': ' + group.name : '', 200, space + 20)
      doc.text(convertToLocaleString(reportData.from, lang, timezone) + ' - ' + convertToLocaleString(reportData.to, lang, timezone), 20, space + 25)

      d.days.forEach(r =>
        data.push([
          r.date.toLocaleDateString(),
          formatNumber(r.distance / 1000),
          r.spentFuel > 0 ? formatNumber(r.spentFuel) : 0,
          formatNumber(lang === 'pt-BR' ? r.avgConsumption.byLiters : r.avgConsumption.byKms),
          convertMS(r.duration),
          formatNumber((r.endOdometer / 1000)),
          formatNumber(r.refueling)
        ]))

      const footValues = [
        'Total:' + d.days.length,
        getTotalKms(userData.user, d.days),
        getTotalConsumption(userData.user, d.days),
        getTotalAvgConsumption(userData.user, d.days),
        getTotalDuration(userData.user, d.days),
        '',
        getTotalRefueling(userData.user, d.days)
      ]

      const style = getStyle(getUserPartner(userData.user))
      addTable(doc, headers, data, footValues, style, space + (userData.allWeek ? 40 : 45),
        {
          1: { halign: 'right' },
          2: { halign: 'right' },
          3: { halign: 'right' },
          4: { halign: 'right' },
          5: { halign: 'right' },
          6: { halign: 'right' }
        })
    })

    return doc
  }
}

function deviceName (device) {
  return device.name + (device.attributes.license_plate ? ', ' + device.attributes.license_plate : '') + (device.model ? ', ' + device.model : '')
}

function getTotalKms (user, data) {
  const totalKms = data.reduce((a, b) => a + b.distance, 0)
  return formatNumber(totalKms / 1000, user.attributes.lang)
}

function getTotalConsumption (user, data) {
  const totalConsumption = data.reduce((a, b) => a + (b.spentFuel > 0 ? b.spentFuel : 0), 0)
  return formatNumber(totalConsumption, user.attributes.lang)
}

function getTotalDuration (user, data) {
  const totalDuration = data.reduce((a, b) => a + b.duration, 0)
  return convertMS(totalDuration)
}

function getTotalRefueling (user, data) {
  const totalRefueling = data.reduce((a, b) => a + b.refueling, 0)
  return formatNumber(totalRefueling, user.attributes.lang)
}

function getTotalAvgConsumption (user, data) {
  const values = data.map(a => a.distance * (user.attributes.lang === 'pt-BR' ? a.avgConsumption.byLiters : a.avgConsumption.byKms)).reduce((a, b) => a + b, 0)
  const totalKms = data.reduce((a, b) => a + b.distance, 0)

  const totalAvgConsumption = values / totalKms
  return formatNumber(totalAvgConsumption, user.attributes.lang)
}

exports.createFuelConsumptionReport = createFuelConsumptionReport
exports.exportFuelConsumptionReportToPDF = exportFuelConsumptionReportToPDF
exports.exportFuelConsumptionReportToExcel = exportFuelConsumptionReportToExcel
exports.create = createFuelConsumptionReport
exports.exportToPDF = exportFuelConsumptionReportToPDF
exports.exportToExcel = exportFuelConsumptionReportToExcel
exports.getCanAvgConsumption = getCanAvgConsumption
